<script lang="ts">
  import Menu from "lucide-svelte/icons/menu";
  import Search from "lucide-svelte/icons/search";
  import { Folder, FolderOpen, Route } from "lucide-svelte";
  import { Button } from "$lib/components/ui/button/index.js";
  import { Input } from "$lib/components/ui/input/index.js";
  import * as Sheet from "$lib/components/ui/sheet/index.js";
  import { t } from "svelte-i18n";
  import Sun from "svelte-radix/Sun.svelte";
  import Moon from "svelte-radix/Moon.svelte";
  import { toggleMode } from "mode-watcher";
  import UserNav from "./user-nav.svelte";
  import { location, push } from "svelte-spa-router";
  import { Separator } from "$lib/components/ui/separator/index.js";
  import type { AuthContext } from "$src/infrastructure/accounts/AuthContext";
  import { ContextTokens } from "../contexts/ContextTokens";
  import { link } from "svelte-spa-router";
  import active from "svelte-spa-router/active";
  import { getContext, onMount } from "svelte";
  import { isEditActive } from "../../application/main/mainTemplateStore";
  import { fly } from "svelte/transition";
  import { HubService } from "$src/application/realtime/HubService";
  import { get } from "svelte/store";
  import { hubStore } from "$src/application/realtime/hubStore";
  import PageNotifier from "./realtime/PageNotifier.svelte";
  import RouteLink from "./components/route-link.svelte";

  const activeClass =
    "text-foreground transition-colors hover:text-foreground whitespace-nowrap";
  const inactiveClass =
    "text-muted-foreground transition-colors hover:text-foreground whitespace-nowrap";

  let { activeGroupStore, groupStoresAccess }: AuthContext = getContext(
    ContextTokens.Auth
  );
  export let isEmbedded: boolean = false;

  let headerDiv: HTMLDivElement;
  let headerHasContent = false;
  $: headerHasContent = headerDiv && headerDiv.children.length > 0;

  let footerDiv: HTMLDivElement;
  let footerHasContent = false;
  $: footerHasContent = footerDiv && footerDiv.children.length > 0;

  let open = false;
  let searchQuery = "";

  onMount(() => {
    notifyJoinPage();
  });

  function closeSheet() {
    open = false;
  }

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      performSearch();
    }
  }

  function performSearch() {
    if (searchQuery.trim() !== "") {
      const queryParams = new URLSearchParams();
      queryParams.set("q", searchQuery);

      const searchUrl = `/gs/${$activeGroupStore?.groupStoreId}/search?${queryParams.toString()}`;

      push(searchUrl);
    } else {
      console.log("Entrez un terme de recherche valide.");
    }
  }

  //real time notification

  let hub: HubService | null = null;

  $: {
    hub = get(hubStore);
    //hub?.getConnection().onreconnected(notifyJoinPageReconnect);
  }

  // function notifyJoinPageReconnect() {
  //   var user = $me;
  //   console.log('reconnected ! >>> join page ', pageUrl, ' user :', user?.firstname);
  //   if( user )
  //     hub?.joinPage(pageUrl, user);
  // }

  function notifyJoinPage() {}
</script>

{#if $activeGroupStore?.groupStoreId}
  <PageNotifier />
  <div class="flex {isEmbedded ? 'h-dvh' : 'min-h-dvh'} flex-col">
    <header
      class="sticky w-full top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-30"
    >
      <div></div>
      <nav
        class="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6"
      >
        <RouteLink
          href={`/gs/${$activeGroupStore?.groupStoreId}/projects`}
          class="flex items-center gap-2 text-lg font-semibold md:text-base"
        >
          <div class="logo w-9 h-9 bg-center"></div>
        </RouteLink>

        <div class="flex h-5 items-center space-x-4">
          {#if ($groupStoresAccess?.groupStores.length ?? 0) > 1 && !$isEditActive}
            <Button href="/#/gs" variant="outline" size="icon">
              {#if $location == "/gs"}
                <FolderOpen class="h-4 w-4" />
              {:else}
                <Folder class="h-4 w-4" />
              {/if}
            </Button>
          {/if}
          <div class="text-nowrap text-lg font-semibold">
            {$activeGroupStore?.groupStoreName}
          </div>
          <Separator orientation="vertical" />
        </div>

        <RouteLink
          href={`/gs/${$activeGroupStore?.groupStoreId}/projects`}
          activeOptions={{
            path: /^\/gs\/[^\/]+\/projects/,
            inactiveClassName: inactiveClass,
            className: activeClass,
          }}
        >
          {$t("page.projects.nav")}
        </RouteLink>
        <RouteLink
          href="/settings"
          activeOptions={{
            path: "/settings",
            inactiveClassName: inactiveClass,
            className: activeClass,
          }}
        >
          {$t("page.settings.nav")}
        </RouteLink>
      </nav>
      <Sheet.Root bind:open>
        <Sheet.Trigger asChild let:builder>
          <Button
            variant="outline"
            size="icon"
            class="shrink-0 md:hidden"
            builders={[builder]}
          >
            <Menu class="h-5 w-5" />
            <span class="sr-only">Toggle navigation menu</span>
          </Button>
        </Sheet.Trigger>
        <Sheet.Content side="left">
          <nav class="grid gap-6 text-lg font-medium">
            <RouteLink
              href={`/gs/${$activeGroupStore?.groupStoreId}/projects`}
              class="flex items-center gap-2 text-lg font-semibold"
            >
              <div class="logo w-9 h-9 bg-center"></div>
            </RouteLink>

            <div class="flex h-5 items-center space-x-4 justify-center">
              <div class="text-lg font-semibold">
                {$activeGroupStore?.groupStoreName}
              </div>
              {#if ($groupStoresAccess?.groupStores.length ?? 0) > 1 && !$isEditActive}
                <Button
                  href="/#/gs"
                  variant="outline"
                  size="icon"
                  on:click={closeSheet}
                >
                  <Folder class="h-4 w-4" />
                </Button>
              {/if}
            </div>

            <RouteLink
              href={`/gs/${$activeGroupStore?.groupStoreId}/projects`}
              activeOptions={{
                path: /^\/gs\/[^\/]+\/projects/,
                inactiveClassName: inactiveClass,
                className: activeClass,
              }}
              on:click={closeSheet}
            >
              {$t("page.projects.nav")}
            </RouteLink>

            <RouteLink
              href="/settings"
              activeOptions={{
                path: "/settings",
                inactiveClassName: inactiveClass,
                className: activeClass,
              }}
              on:click={closeSheet}
            >
              {$t("page.settings.nav")}
            </RouteLink>
          </nav>
        </Sheet.Content>
      </Sheet.Root>
      <div class="flex w-full items-center gap-4 md:ml-auto md:gap-2 lg:gap-4">
        <form class="ml-auto flex-1 sm:flex-initial">
          <div class="relative">
            <Search
              class="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            />
            <Input
              type="search"
              placeholder="{$t('global.menu.search')}..."
              disabled={$isEditActive}
              class="pl-8 sm:w-[300px] md:w-[200px] lg:w-[300px]"
              bind:value={searchQuery}
              on:keydown={handleKeyPress}
            />
          </div>
        </form>
        <Button on:click={toggleMode} variant="outline" size="icon">
          <Sun
            class="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0"
          />
          <Moon
            class="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100"
          />
          <span class="sr-only">Toggle theme</span>
        </Button>
        <UserNav />
      </div>
    </header>
    <div
      class="sticky w-full top-16 z-20 text-secondary-foreground bg-secondary"
    >
      <div bind:this={headerDiv}
        class="container flex flex-row justify-between items-center h-12 overflow-hidden {headerHasContent ? '' : 'hidden'}"
      >
        <slot name="menu-top-left"></slot>
        <slot name="menu-top-center"></slot>
        <slot name="menu-top-right"></slot>
      </div>
    </div>

    <main
      class="flex flex-1 flex-col {isEmbedded
        ? 'overflow-hidden'
        : ''} gap-4 bg-muted/40 p-1 md:gap-8 md:p-6"
    >
      <slot />
    </main>

    <div  class="sticky bottom-0 w-full z-20 ">
      <div class="sticky bottom-0  ">
        <slot name="menu-info-bottom" />
      </div>

      {#if $isEditActive}
        <div
          class="fixed bottom-0 left-0 right-0 bg-primary text-primary-foreground z-30 h-14"
          transition:fly={{ y: 100, duration: 300 }}
        >
          <slot name="menu-action-bottom" />
        </div>
      {/if}
      <div bind:this={footerDiv} class="bg-secondary text-secondary-foreground md:hidden h-14 {footerHasContent ? '' : 'hidden'}">
        <slot name="menu-bottom" />
      </div>
    </div>
  </div>
{/if}

<style>
  .logo {
    background-image: var(--logo);
    background-size: contain;
    background-repeat: no-repeat;
  }
</style>
